import React, { useEffect, useMemo, useState } from 'react';

import { sancionadosData } from './sancionadosData2026';

import './sancionados.css';

const PAGE_SIZE = 50;

const normalizar = (texto = '') =>
  texto
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const mostrar = (valor) => {
  if (!valor) return 'N/A';

  const limpio = valor.toString().trim();

  if (
    limpio === '' ||
    limpio.toLowerCase() === 'n/a' ||
    limpio.toLowerCase() === 'na'
  ) {
    return 'N/A';
  }

  return limpio;
};

const Sancionados = () => {
  const [busqueda, setBusqueda] = useState('');
  const [dependencia, setDependencia] = useState('');
  const [sancion, setSancion] = useState('');
  const [pagina, setPagina] = useState(1);
  const [seleccionado, setSeleccionado] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const dependencias = useMemo(() => {
    return [
      ...new Set(
        sancionadosData
          .map((item) => item.dependencia)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b, 'es'));
  }, []);

  const sanciones = useMemo(() => {
    return [
      ...new Set(
        sancionadosData
          .map((item) => item.sancion)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b, 'es'));
  }, []);

  const filtrados = useMemo(() => {
    const termino = normalizar(busqueda);

    return [...sancionadosData]
      .filter((item) => {
        const coincideBusqueda =
          !termino ||
          normalizar(item.servidorPublico).includes(termino) ||
          normalizar(item.dependencia).includes(termino) ||
          normalizar(item.expedienteNUC).includes(termino) ||
          normalizar(item.autoridadSancionadora).includes(termino) ||
          normalizar(item.causaSancion).includes(termino) ||
          normalizar(item.sancion).includes(termino);

        const coincideDependencia =
          !dependencia || item.dependencia === dependencia;

        const coincideSancion =
          !sancion || item.sancion === sancion;

        return coincideBusqueda && coincideDependencia && coincideSancion;
      })
      .sort((a, b) => Number(b.numero) - Number(a.numero));
  }, [busqueda, dependencia, sancion]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda, dependencia, sancion]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(filtrados.length / PAGE_SIZE)
  );

  const inicio = (pagina - 1) * PAGE_SIZE;

  const visibles = filtrados.slice(
    inicio,
    inicio + PAGE_SIZE
  );

  const limpiarFiltros = () => {
    setBusqueda('');
    setDependencia('');
    setSancion('');
    setPagina(1);
  };

  const descargarCSV = () => {
    const encabezados = [
      'No.',
      'Servidor Público',
      'Dependencia',
      'Sanción',
      'Duración',
      'Expediente NUC',
      'Autoridad Sancionadora',
      'Fecha de Resolución',
      'Causa de la Sanción',
      'Sanción económica',
      'Fecha Inicio de la Sanción',
      'Fecha Final de la Sanción',
    ];

    const filas = filtrados.map((item) => [
      item.numero,
      item.servidorPublico,
      item.dependencia,
      item.sancion,
      item.duracion,
      item.expedienteNUC,
      item.autoridadSancionadora,
      item.fechaResolucion,
      item.causaSancion,
      item.sancionEconomica,
      item.fechaInicioSancion,
      item.fechaFinalSancion,
    ]);

    const escapar = (valor) => {
      const texto = (valor ?? '').toString().replace(/"/g, '""');
      return `"${texto}"`;
    };

    const csv = [
      encabezados.map(escapar).join(','),
      ...filas.map((fila) =>
        fila.map(escapar).join(',')
      ),
    ].join('\n');

    const blob = new Blob(
      ['\uFEFF' + csv],
      {
        type: 'text/csv;charset=utf-8;',
      }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      'personas_servidoras_publicas_sancionadas_2026.csv'
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const irPrimeraPagina = () => {
    setPagina(1);
  };

  const irPaginaAnterior = () => {
    setPagina((actual) => Math.max(1, actual - 1));
  };

  const irPaginaSiguiente = () => {
    setPagina((actual) =>
      Math.min(totalPaginas, actual + 1)
    );
  };

  const irUltimaPagina = () => {
    setPagina(totalPaginas);
  };

  return (
    <main className="sancionados-page">
      <div className="sancionados-container">

        <header className="sancionados-header">
          <p className="sancionados-kicker">
            Dirección de Situación Patrimonial
          </p>

          <h1>
            Personas Servidoras Públicas Sancionadas
          </h1>

          <div className="sancionados-title-line">
            <span></span>
          </div>

          <p className="sancionados-description">
            Consulta el registro de personas servidoras públicas
            sancionadas correspondiente al ejercicio 2026.
          </p>
        </header>

        <section className="sancionados-toolbar">

          <div className="sancionados-search">
            <i className="fa-solid fa-magnifying-glass"></i>

            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, dependencia, expediente..."
            />
          </div>

          <div className="sancionados-filter">
            <label>Dependencia</label>

            <select
              value={dependencia}
              onChange={(e) => setDependencia(e.target.value)}
            >
              <option value="">
                Todas las dependencias
              </option>

              {dependencias.map((item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="sancionados-filter">
            <label>Tipo de sanción</label>

            <select
              value={sancion}
              onChange={(e) => setSancion(e.target.value)}
            >
              <option value="">
                Todas las sanciones
              </option>

              {sanciones.map((item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="sancionados-actions">
            <button
              type="button"
              className="sancionados-clear"
              onClick={limpiarFiltros}
            >
              <i className="fa-solid fa-filter-circle-xmark"></i>
              Limpiar
            </button>

            <button
              type="button"
              className="sancionados-download"
              onClick={descargarCSV}
            >
              <i className="fa-solid fa-file-csv"></i>
              Descargar CSV
            </button>
          </div>

        </section>

        <div className="sancionados-summary">
          <div>
            <strong>{filtrados.length}</strong>
            <span>registros encontrados</span>
          </div>

          <div className="sancionados-year">
            Ejercicio 2026
          </div>
        </div>

        <section className="sancionados-table-wrapper">
          <table className="sancionados-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Servidor Público</th>
                <th>Dependencia</th>
                <th>Sanción</th>
                <th>Expediente</th>
                <th>Resolución</th>
                <th>Detalle</th>
              </tr>
            </thead>

            <tbody>
              {visibles.length > 0 ? (
                visibles.map((item) => (
                  <tr key={item.numero}>
                    <td
                      data-label="No."
                      className="sancionados-number"
                    >
                      {item.numero}
                    </td>

                    <td data-label="Servidor Público">
                      <strong>
                        {mostrar(item.servidorPublico)}
                      </strong>
                    </td>

                    <td data-label="Dependencia">
                      {mostrar(item.dependencia)}
                    </td>

                    <td data-label="Sanción">
                      <span className="sancionados-badge">
                        {mostrar(item.sancion)}
                      </span>
                    </td>

                    <td data-label="Expediente">
                      {mostrar(item.expedienteNUC)}
                    </td>

                    <td data-label="Resolución">
                      {mostrar(item.fechaResolucion)}
                    </td>

                    <td data-label="Detalle">
                      <button
                        type="button"
                        className="sancionados-detail"
                        onClick={() => setSeleccionado(item)}
                      >
                        Ver detalle
                        <i className="fa-solid fa-arrow-right"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="sancionados-empty"
                  >
                    No se encontraron registros con los
                    filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <div className="sancionados-pagination">
          <button
            type="button"
            onClick={irPrimeraPagina}
            disabled={pagina === 1}
            title="Ir a la primera página"
          >
            <i className="fa-solid fa-angles-left"></i>
            Primera
          </button>

          <button
            type="button"
            onClick={irPaginaAnterior}
            disabled={pagina === 1}
          >
            <i className="fa-solid fa-chevron-left"></i>
            Anterior
          </button>

          <span>
            Página <strong>{pagina}</strong> de{' '}
            <strong>{totalPaginas}</strong>
          </span>

          <button
            type="button"
            onClick={irPaginaSiguiente}
            disabled={pagina === totalPaginas}
          >
            Siguiente
            <i className="fa-solid fa-chevron-right"></i>
          </button>

          <button
            type="button"
            onClick={irUltimaPagina}
            disabled={pagina === totalPaginas}
            title="Ir a la última página"
          >
            Última
            <i className="fa-solid fa-angles-right"></i>
          </button>
        </div>

        <div className="sancionados-notice">
          <i className="fa-solid fa-circle-info"></i>

          <p>
            La información presentada corresponde al
            registro proporcionado por la Dirección de
            Situación Patrimonial.
          </p>
        </div>

      </div>

      {seleccionado && (
        <div
          className="sancionados-modal-overlay"
          onClick={() => setSeleccionado(null)}
        >
          <div
            className="sancionados-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sancionados-modal-header">
              <div>
                <span>
                  Registro No. {seleccionado.numero}
                </span>

                <h2>
                  {mostrar(seleccionado.servidorPublico)}
                </h2>
              </div>

              <button
                type="button"
                className="sancionados-modal-close"
                onClick={() => setSeleccionado(null)}
                aria-label="Cerrar"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="sancionados-modal-body">
              <Detalle
                label="Dependencia"
                value={seleccionado.dependencia}
              />

              <Detalle
                label="Sanción"
                value={seleccionado.sancion}
              />

              <Detalle
                label="Duración"
                value={seleccionado.duracion}
              />

              <Detalle
                label="Expediente NUC"
                value={seleccionado.expedienteNUC}
              />

              <Detalle
                label="Autoridad Sancionadora"
                value={seleccionado.autoridadSancionadora}
              />

              <Detalle
                label="Fecha de Resolución"
                value={seleccionado.fechaResolucion}
              />

              <Detalle
                label="Causa de la Sanción"
                value={seleccionado.causaSancion}
                full
              />

              <Detalle
                label="Sanción económica"
                value={seleccionado.sancionEconomica}
              />

              <Detalle
                label="Fecha Inicio de la Sanción"
                value={seleccionado.fechaInicioSancion}
              />

              <Detalle
                label="Fecha Final de la Sanción"
                value={seleccionado.fechaFinalSancion}
              />

              <Detalle
                label="Ejercicio"
                value={seleccionado.anio}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

const Detalle = ({ label, value, full = false }) => {
  return (
    <div
      className={`sancionados-detail-item ${
        full ? 'sancionados-detail-full' : ''
      }`}
    >
      <span>{label}</span>
      <p>{mostrar(value)}</p>
    </div>
  );
};

export default Sancionados;
